import PrimaryButton from '../../../components/buttons/PrimaryButton'
import AuthRecoveryFooter from './components/AuthRecoveryFooter'
import AuthRecoveryTopBar from './components/AuthRecoveryTopBar'
import OtpInputGroup from './components/OtpInputGroup'
import {
  OTP_BACKGROUND_IMAGE_URL,
  PASSWORD_RECOVERY_COPY,
} from './constants/passwordRecoveryContent'
import { useOtpVerificationScreen } from './hooks/useOtpVerificationScreen'

function MessageBanner({ message, variant = 'error' }) {
  if (!message) return null

  const className =
    variant === 'error'
      ? 'bg-error/10 text-error'
      : 'bg-primary/10 text-primary'

  return (
    <div className={`rounded-lg px-4 py-3 text-sm ${className}`} role={variant === 'error' ? 'alert' : 'status'}>
      {message}
    </div>
  )
}

export default function OTPVerificationScreen() {
  const {
    email,
    digits,
    formattedTimer,
    isEmailValid,
    isSendingOtp,
    isVerifyingOtp,
    error,
    message,
    inputRefs,
    handleDigitChange,
    handleDigitKeyDown,
    handleDigitPaste,
    handleResendCode,
    handleVerifyOtp,
  } = useOtpVerificationScreen()

  const otpFilled = digits.every((digit) => digit.length === 1)

  return (
    <div className="relative flex min-h-screen flex-col bg-surface text-on-surface">
      <AuthRecoveryTopBar />

      <main className="relative flex flex-1 items-center justify-center px-6 pb-8 pt-20">
        <div className="mx-auto grid w-full max-w-6xl gap-12 md:grid-cols-2 md:items-center">
          <section className="hidden md:block">
            <span className="inline-block rounded-full bg-primary-container/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-primary-container">
              {PASSWORD_RECOVERY_COPY.leftBadge}
            </span>

            <h1 className="mt-8 font-display text-7xl font-extrabold leading-[0.95] tracking-tight text-on-surface">
              Verify your
              <br />
              identity.
            </h1>

            <p className="mt-8 max-w-md text-lg leading-relaxed text-on-surface-variant">
              {PASSWORD_RECOVERY_COPY.otpSubtitle}
            </p>

            <div className="w-80 pt-10">
              <div className="mb-2 flex items-end justify-between">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.trustScoreLabel}
                </span>
                <span className="text-xs font-bold text-primary">
                  {PASSWORD_RECOVERY_COPY.trustScoreValue}%
                </span>
              </div>

              <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${PASSWORD_RECOVERY_COPY.trustScoreValue}%` }}
                />
              </div>
            </div>
          </section>

          <section className="mx-auto w-full max-w-md rounded-xl bg-surface-container-lowest p-10 shadow-ambient">
            <div className="mb-10">
              <h2 className="font-display text-[42px] font-bold text-on-surface">
                {PASSWORD_RECOVERY_COPY.otpCardTitle}
              </h2>
              <p className="mt-2 text-sm text-on-surface-variant">
                {PASSWORD_RECOVERY_COPY.otpCardSubtitle}
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleVerifyOtp}>
              <MessageBanner message={error} variant="error" />
              <MessageBanner message={message} variant="info" />

              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.emailLabel}
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[20px] text-outline">
                    mail
                  </span>
                  <input
                    type="email"
                    value={email}
                    placeholder={PASSWORD_RECOVERY_COPY.emailPlaceholder}
                    readOnly
                    className="h-14 w-full rounded-full border border-surface-variant bg-surface-container-low pl-11 pr-4 text-base text-on-surface outline-none"
                    disabled
                  />
                </div>
              </div>

              <OtpInputGroup
                digits={digits}
                onDigitChange={handleDigitChange}
                onDigitKeyDown={handleDigitKeyDown}
                onDigitPaste={handleDigitPaste}
                inputRefs={inputRefs}
                disabled={isVerifyingOtp}
              />

              <PrimaryButton
                type="submit"
                loading={isVerifyingOtp}
                disabled={!isEmailValid || !otpFilled || isSendingOtp}
                className="rounded-full"
              >
                {PASSWORD_RECOVERY_COPY.otpVerifyCta}
              </PrimaryButton>
            </form>

            <div className="mt-8">
              <div className="flex items-center justify-center gap-2 text-on-surface-variant">
                <span className="material-symbols-outlined text-[17px]">schedule</span>
                <span className="text-xs font-medium uppercase tracking-[0.12em]">
                  Expires in {formattedTimer}
                </span>
              </div>

              <div className="mt-6 flex items-center gap-3 border-t border-surface-variant/30 pt-6">
                <span className="text-[15px] text-on-surface-variant">
                  {PASSWORD_RECOVERY_COPY.resendLabel}
                </span>
                <button
                  type="button"
                  className="text-[15px] font-bold text-primary transition hover:underline disabled:cursor-not-allowed disabled:text-outline"
                  onClick={() => handleResendCode()}
                  disabled={isSendingOtp || isVerifyingOtp}
                >
                  {isSendingOtp ? 'Sending...' : PASSWORD_RECOVERY_COPY.resendCta}
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      <AuthRecoveryFooter />

      <div className="pointer-events-none fixed inset-0 -z-10 opacity-5">
        <img
          src={OTP_BACKGROUND_IMAGE_URL}
          alt=""
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  )
}
